import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { TURNAROUND_PHASE_3_FEATURE_FLAG } from 'featureFlags';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { useAddTurnaroundImplantsToDelivery } from 'api/hooks/useAddTurnaroundImplantsToDelivery';
import { useAddTurnaroundSetToDelivery } from 'api/hooks/useAddTurnaroundSetToDelivery';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import Button from 'components/Button';
import { formatDate } from 'components/DatePicker/DateInput';
import { useLanguage } from 'context/AppContext';
import { useTurnaroundDestinationDeliveryId } from '../hooks/useTurnaroundDestinationDeliveryId';
import { useTurnaroundDestinationEntities } from '../hooks/useTurnaroundDestinationEntities';
import { useTurnaroundOriginDeliveryId } from '../hooks/useTurnaroundOriginDeliveryId';
import { TurnaroundEntityImplant, TurnaroundEntitySet } from '../models';
import { TurnaroundDestinationEntitiesAlreadyPresentDialog } from './TurnaroundDestinationEntitiesAlreadyPresentDialog';
import { TurnaroundDestinationSuccessDialog } from './TurnaroundDestinationSuccessDialog';
import { TurnaroundOriginEntitiesAlreadyTurnedAroundDialog } from './TurnaroundOriginEntitiesAlreadyTurnedAroundDialog';

type Props = ComponentProps<typeof Button>;

type VisibleDialog =
  | {
      type: 'alreadyAdded';
      setNames: string[];
      hasImplants: boolean;
    }
  | {
      type: 'alreadyTurnedAround';
      setNames: string[];
      hasImplants: boolean;
    }
  | {
      type: 'success';
      setNames: string[];
      hasImplants: boolean;
    };

export const TurnaroundExistingSurgeryButton = ({ ...props }: Props) => {
  const lang = useLanguage()[0].TurnaroundExistingSurgeryButton;

  const [turnaroundOriginDeliveryId] = useTurnaroundOriginDeliveryId();
  const [turnaroundDestinationDeliveryId] = useTurnaroundDestinationDeliveryId();
  const [turnaroundDestinationEntities] = useTurnaroundDestinationEntities();
  const [visibleDialog, setVisibleDialog] = useState<VisibleDialog | null>(null);

  const { data: originDelivery } = useDelivery(turnaroundOriginDeliveryId);
  const { data: destinationDelivery } = useDelivery(turnaroundDestinationDeliveryId);
  const { data: currentUser } = useCurrentUser();

  const { mutateAsync: addTurnaroundSetToDelivery, isLoading: isAddingTurnaroundSetToDelivery } =
    useAddTurnaroundSetToDelivery();
  const { mutateAsync: addTurnaroundImplantsToDelivery, isLoading: isAddingTurnaroundImplantsToDelivery } =
    useAddTurnaroundImplantsToDelivery();

  const turnaroundDestinationSetKitInstanceIds = useMemo(
    () =>
      turnaroundDestinationEntities
        .filter(entity => entity.type === 'set')
        .map(entity => (entity as TurnaroundEntitySet).setKitInstanceId),
    [turnaroundDestinationEntities],
  );

  const turnaroundDestinationImplantInstanceIds = useMemo(
    () =>
      turnaroundDestinationEntities
        .filter(entity => entity.type === 'implant')
        .map(entity => (entity as TurnaroundEntityImplant).implantInstanceId),
    [turnaroundDestinationEntities],
  );

  const turnaroundSetNames = turnaroundDestinationSetKitInstanceIds.map(
    destinationSetKitInstanceId =>
      originDelivery?.sets.find(set => set.kitInstanceId === destinationSetKitInstanceId)?.displayCode || '',
  );

  const turnaroundToExistingSurgery = useCallback(async () => {
    if (!turnaroundDestinationDeliveryId) {
      throw new Error('No destination delivery id');
    }

    if (!turnaroundOriginDeliveryId) {
      throw new Error('Turnaround origin delivery ID is not defined');
    }

    if (!originDelivery) {
      throw new Error('Origin delivery is not defined');
    }

    if (!currentUser) {
      throw new Error('Current user is not defined');
    }

    // add the sets to the destination delivery in sequence to avoid race conditions
    for (const setKitInstanceId of turnaroundDestinationSetKitInstanceIds) {
      await addTurnaroundSetToDelivery({
        deliveryId: turnaroundDestinationDeliveryId,
        request: { kit_instance_id: setKitInstanceId },
      });
    }

    // add any implants to the destination delivery
    if (turnaroundDestinationImplantInstanceIds.length) {
      await addTurnaroundImplantsToDelivery({
        deliveryId: turnaroundDestinationDeliveryId.toString(),
        originDeliveryId: turnaroundOriginDeliveryId.toString(),
      });
    }

    await trackEvent(EventType.TurnaroundExistingSurgery, {
      current_user: currentUser.userId,
      tenant_name: currentUser.tenantName,
      supplier: originDelivery.supplier.name || '',
      order_number: originDelivery.orderNumber || '',
      surgery_reference: originDelivery.surgery.surgeryReference || '',
      delivery_number: turnaroundOriginDeliveryId.toString(),
      surgeon: originDelivery.surgery?.surgeonName || '',
      surgery_date: originDelivery.surgery?.surgeryDate ? formatDate(originDelivery.surgery.surgeryDate) : '',
      set_ids: turnaroundDestinationSetKitInstanceIds,
      has_implants: Boolean(turnaroundDestinationImplantInstanceIds.length),
    });
  }, [
    turnaroundDestinationDeliveryId,
    turnaroundOriginDeliveryId,
    originDelivery,
    currentUser,
    turnaroundDestinationImplantInstanceIds,
    turnaroundDestinationSetKitInstanceIds,
    addTurnaroundSetToDelivery,
    addTurnaroundImplantsToDelivery,
  ]);

  const turnaroundToExistingSurgeryAndShowSuccessDialog = useCallback(async () => {
    await turnaroundToExistingSurgery();

    setVisibleDialog({
      type: 'success',
      setNames: turnaroundSetNames,
      hasImplants: Boolean(turnaroundDestinationImplantInstanceIds.length),
    });
  }, [turnaroundToExistingSurgery, turnaroundSetNames, turnaroundDestinationImplantInstanceIds]);

  return (
    <>
      <Button
        colors="white on active"
        disabled={
          !turnaroundDestinationDeliveryId ||
          !turnaroundDestinationEntities.length ||
          isAddingTurnaroundSetToDelivery ||
          isAddingTurnaroundImplantsToDelivery
        }
        loading={isAddingTurnaroundSetToDelivery || isAddingTurnaroundImplantsToDelivery}
        onClick={async () => {
          if (TURNAROUND_PHASE_3_FEATURE_FLAG) {
            // Now that we allow pending turnarounds to be turned around multiple times, we first need to check if we already turned around the set/implants to this delivery
            // If they have, then we show the TurnaroundDestinationEntitiesAlreadyPresentDialog
            // NOTE: We can't compare the kit instance ids directly because they are unique per delivery, we therefore compare it with a turnaroundOriginSetKitInstanceId
            const pendingTurnaroundSetInDestinationDelivery = destinationDelivery?.sets.find(
              set =>
                set.turnaroundOriginSetKitInstanceId &&
                turnaroundDestinationSetKitInstanceIds.includes(set.turnaroundOriginSetKitInstanceId),
            );

            // Implants are either all turned around or none turned around so we can simply compare the turnaroundOriginDeliveryId with the originDeliveryId
            const hasPendingTurnaroundImplantsInDestinationDelivery = destinationDelivery?.implants?.some(
              implant => implant.turnaroundOriginDeliveryId === turnaroundOriginDeliveryId,
            );

            if (pendingTurnaroundSetInDestinationDelivery || hasPendingTurnaroundImplantsInDestinationDelivery) {
              setVisibleDialog({
                type: 'alreadyAdded',
                setNames: pendingTurnaroundSetInDestinationDelivery
                  ? [pendingTurnaroundSetInDestinationDelivery.displayCode]
                  : [],
                hasImplants: Boolean(hasPendingTurnaroundImplantsInDestinationDelivery),
              });

              return;
            }

            // If the sets/implants have already been turned around to a different surgery, show the TurnaroundOriginEntitiesAlreadyTurnedAroundDialog
            const pendingTurnaroundSetsInOriginDelivery = originDelivery?.sets.filter(
              set => set.turnaroundOrigin && set.turnaroundStage === 'PENDING',
            );
            const hasPendingTurnaroundImplantsInOriginDelivery = originDelivery?.implants?.some(
              implant => implant.turnaroundOrigin && implant.turnaroundStage === 'PENDING',
            );

            if (pendingTurnaroundSetsInOriginDelivery?.length) {
              setVisibleDialog({
                type: 'alreadyTurnedAround',
                setNames: pendingTurnaroundSetsInOriginDelivery.map(set => set.displayCode),
                hasImplants: Boolean(hasPendingTurnaroundImplantsInOriginDelivery),
              });

              return;
            }
          }

          // Otherwise just add the sets and implants as normal
          await turnaroundToExistingSurgeryAndShowSuccessDialog();
        }}
        {...props}
      >
        {lang.existingSurgery}
      </Button>

      {visibleDialog?.type === 'alreadyAdded' && (
        <TurnaroundDestinationEntitiesAlreadyPresentDialog
          destinationDeliveryId={turnaroundDestinationDeliveryId}
          setNames={visibleDialog.setNames}
          hasImplants={visibleDialog.hasImplants}
          onCancel={() => setVisibleDialog(null)}
        />
      )}

      {visibleDialog?.type === 'alreadyTurnedAround' && (
        <TurnaroundOriginEntitiesAlreadyTurnedAroundDialog
          setNames={visibleDialog.setNames}
          hasImplants={visibleDialog.hasImplants}
          onConfirm={async () => {
            await turnaroundToExistingSurgeryAndShowSuccessDialog();
          }}
          onCancel={() => setVisibleDialog(null)}
        />
      )}

      {visibleDialog?.type === 'success' && (
        <TurnaroundDestinationSuccessDialog
          destinationDeliveryId={turnaroundDestinationDeliveryId}
          onClose={() => setVisibleDialog(null)}
        />
      )}
    </>
  );
};
